import { __ } from "@wordpress/i18n";
import apiFetch from "@wordpress/api-fetch";
import {
    useState,
    useCallback,
    useEffect,
    useMemo,
    createContext,
} from "@wordpress/element";
import {
    Settings,
    Settings2,
    PackagePlus,
    TextCursorInput,
    Diff,
    UnfoldHorizontal,
    OctagonAlert,
} from "lucide-react";
import { useToast } from "../hook/useToast";

apiFetch.use(apiFetch.createNonceMiddleware(WPBEAN_QC_API.nonce));

export const SettingsContext = createContext({
    hasChanges: null,
    loading: {},
    tabs: [],
    activeTab: null,
    setActiveTab: () => {},
    settings: null,
    setSettings: () => {},
    sidebarOpen: null,
    setSidebarOpen: () => {},
    handleToggle: () => {},
    handleInputChange: () => {},
    handleSpaceControlChange: () => {},
    handleSubmit: () => {},
    getSection: () => {},
    getOption: () => {},
    quantity: null,
    setQuantity: () => {},
    showMinAlert: false,
    setShowMinAlert: () => {},
    showMaxAlert: false,
    setShowMaxAlert: () => {},
    previewOptions: null,
    handleDecrease: () => {},
    handleIncrease: () => {},
    handleQtyInputChange: () => {},
});

// Helper to process mapping
// Supports two formats:
// 1) Simple: { fieldName: defaultValue } — srcKey defaults to fieldName
// 2) Advanced: { fieldName: [srcKey, defaultVal] } — explicit source key
const processSettings = (source, map) =>
    Object.fromEntries(
        Object.entries(map).map(([key, config]) => {
            let srcKey;
            let defaultVal;

            // Detect which format is used
            if (Array.isArray(config)) {
                // Advanced format: [srcKey, defaultVal]
                [srcKey, defaultVal] = config;
            } else {
                // Simple format: just the default value, srcKey = key
                srcKey = key;
                defaultVal = config;
            }

            let val = source?.[srcKey];

            if (typeof val === "undefined") val = defaultVal;

            return [key, val];
        }),
    );

function deepEqual(obj1, obj2) {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
}

export function SettingsContextProvider({ children }) {
    const { addToast, removeToast, clearAllToasts, toasts } = useToast();
    const [loading, setLoading] = useState({
        settings: true, // fetch settings loading
        updateSettings: false, // update settings loading
        formsDataFetch: false,
    });
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const STORAGE_KEY = "wpb_quote_settings_active_tab";
    const [activeTab, setActiveTabState] = useState(() => {
        try {
            if (typeof window === "undefined") return "general";
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? saved : "general";
        } catch (e) {
            return "general";
        }
    });
    const [qtyValidationMessages, setQtyValidationMessages] = useState([]);

    // Helper to get the full option section key with standard prefix
    const getSection = (section, prefix = "wpbean_qc_settings_") => {
        if (!section) {
            return;
        }

        return prefix + section;
    };

    // Helper to get an option value from settings
    // Usage: getOption("fieldName", "tooltip") or getOption("fieldName") if using in TooltipSettings
    const getOption = (fieldName, sectionId) => {
        if (!fieldName) {
            return undefined;
        }

        // If sectionId not provided, return a function that lets you specify it later
        // or return undefined if we can't determine the section
        if (!sectionId) {
            return undefined;
        }

        const fullSectionKey = getSection(sectionId);
        return settings?.[fullSectionKey]?.[fieldName];
    };

    const setActiveTab = useCallback((tabId) => {
        try {
            if (typeof window !== "undefined") {
                localStorage.setItem(STORAGE_KEY, tabId);
            }
        } catch (e) {
            // ignore localStorage errors
        }
        setActiveTabState(tabId);
    }, []);

    // Default General Settings
    const initialGeneralSettings = {
        productPage: true,
        shopPage: false,
        archivePage: false,
        cartPage: false,
        qtyDisplayType: "buttons",
        btnSkin: "type1",
    };

    // Default Quantity Settings
    const initialQuantitySettings = {
        universalQuantity: true,
        quantityMin: 1,
        quantityMax: 100,
        quantityStep: 1,
        defaultQuantity: 1,
        allowDecimal: false,
        allowManualInput: true,
        validateManualInput: false,
    };

    // Default Button Settings
    const initialInputSettings = {
        qtyWidth: "",
        qtyHeight: "",
        borderRadius: "",
        borderWidth: "",
        borderColor: "",
        borderHoverColor: "",
        qtyColor: "",
        qtyHoverColor: "",
        btnBgColor: "",
        btnBgHoverColor: "",
        qtyFontSize: "",
        qtyFontWeight: "",
        qtyPadding: {},
        qtyMargin: {},
    };

    // Default Icon Settings
    const initialIconSettings = {
        iconsType: "plus_minus",
        iconAreaWidth: "",
        iconAreaHeight: "",
        iconsSize: "",
        iconColor: "",
        iconHoverColor: "",
        iconBg: "",
        iconHoverBg: "",
        iconsBorderWidth: "",
        iconsRadius: "",
        iconsBorderColor: "",
        iconsBorderHoverColor: "",
    };

    // Default Slider Settings
    const initialSlideripSettings = {
        showInput: true,
        thumbQuantity: true,
        inputPosition: "top",
        showTooltip: false,
        tooltipFormat: __(
            "Step {step}, Min Qty: {min}, Max Qty: {max}",
            "wpbean-quantity-controls-for-woocommerce",
        ),
        thumbSize: "24px",
        sliderWidth: "100%",
        sliderHeight: "8px",
        thumbBgColor: "#2563eb",
        thumbTextColor: "#ffffff",
        trackColor: "#efefef",
        inputColor: "#374151",
        thumbFontSize: "12px",
        thumbFontWeight: "600",
        inputFontSize: "14px",
        inputFontWeight: "600",
        sliderMargin: { bottom: "1.75rem" },
        sliderGap: "15px",
    };

    // Default Error Messages Settings
    const initialMessagesSettings = {
        minMessage: __(
            "Minimum quantity for %title% is %min%.",
            "wpbean-quantity-controls-for-woocommerce",
        ),
        maxMessage: __(
            "Maximum quantity for %title% is %max%.",
            "wpbean-quantity-controls-for-woocommerce",
        ),
        stepMessage: __(
            "Please enter a valid value. The two nearest valid values are %nearestA% and %nearestB%.",
            "wpbean-quantity-controls-for-woocommerce",
        ),
    };

    // Default Prefix & Suffix Settings
    const initialPrefixSuffixSettings = {
        prefixText: "",
        suffixText: "",
        prefixFontSize: "",
        prefixFontWeight: "",
        prefixAlignment: "left",
        prefixMargin: {},
        suffixMargin: {},
    };

    const initialSettingsData = {
        wpbean_qc_settings_general: initialGeneralSettings,
        wpbean_qc_settings_quantity: initialQuantitySettings,
        wpbean_qc_settings_input: initialInputSettings,
        wpbean_qc_settings_slider: initialSlideripSettings,
        wpbean_qc_settings_messages: initialMessagesSettings,
        wpbean_qc_settings_icon: initialIconSettings,
        wpbean_qc_settings_prefixsuffix: initialPrefixSuffixSettings,
    };
    const [originalSettings, setOriginalSettings] =
        useState(initialSettingsData);
    const [settings, setSettings] = useState(initialSettingsData);
    const [hasChanges, setHasChanges] = useState(false);
    const [fetchedSettings, setFetchedSettings] = useState(null);

    // Helper function for button preview in the settings [Button Preview]
    const previewOptions = useMemo(() => {
        const toNumber = (v) => {
            const n = Number(v);
            return Number.isFinite(n) ? n : v;
        };

        const minNum = toNumber(
            getOption("quantityMin", "quantity") ??
                initialQuantitySettings.quantityMin,
        );
        const maxNum = toNumber(
            getOption("quantityMax", "quantity") ??
                initialQuantitySettings.quantityMax,
        );
        const stepNum = toNumber(
            getOption("quantityStep", "quantity") ??
                initialQuantitySettings.quantityStep,
        );

        const defaultNum = toNumber(
            getOption("defaultQuantity", "quantity") ??
                initialQuantitySettings.defaultQuantity,
        );

        return {
            initialValue: minNum,
            step: stepNum,
            minValue: minNum,
            maxValue: maxNum,
            defaultValue: defaultNum,
        };
    }, [settings]);

    // Ensure `quantity` respects current preview bounds when previewOptions change
    useEffect(() => {
        setQuantity((current) => {
            let next = previewOptions.defaultValue;
            if (next < previewOptions.minValue) next = previewOptions.minValue;
            if (
                typeof previewOptions.maxValue === "number" &&
                next > previewOptions.maxValue
            )
                next = previewOptions.maxValue;
            return next;
        });
    }, [
        previewOptions.minValue,
        previewOptions.maxValue,
        previewOptions.defaultValue,
    ]);
    const [quantity, setQuantity] = useState(previewOptions.initialValue);
    const [allowFractional, setAllowFractional] = useState(false);
    // Raw option values (can be string/number) — kept for reference
    const quantityMax = getOption("quantityMax", "quantity");
    const quantityMin = getOption("quantityMin", "quantity");

    // Alert flags when hitting bounds in preview
    const [showMinAlert, setShowMinAlert] = useState(false);
    const [showMaxAlert, setShowMaxAlert] = useState(false);

    const handleDecrease = () => {
        const newValue = quantity - previewOptions.step;
        if (newValue >= previewOptions.minValue) {
            setQuantity(newValue);
            // Clear min alert when moving back into range
            setShowMinAlert(false);
            setShowMaxAlert(false);
        } else {
            // Hitting below minimum — show min alert
            setShowMinAlert(true);
        }
    };

    const handleIncrease = () => {
        const newValue = quantity + previewOptions.step;
        if (
            previewOptions.maxValue === null ||
            newValue <= previewOptions.maxValue
        ) {
            setQuantity(newValue);
            // Clear max alert when moving back into range
            setShowMaxAlert(false);
            setShowMinAlert(false);
        } else {
            // Hitting above maximum — show max alert
            setShowMaxAlert(true);
        }
    };

    const handleQtyInputChange = (e) => {
        const allowDecimal = getOption("allowDecimal", "quantity");
        const value = allowDecimal
            ? parseFloat(e.target.value)
            : parseInt(e.target.value);

        if (!isNaN(value)) {
            if (
                value >= previewOptions.minValue &&
                (previewOptions.maxValue === null ||
                    value <= previewOptions.maxValue)
            ) {
                setQuantity(value);
                // Clear any alerts when user enters a valid value
                setShowMinAlert(false);
                setShowMaxAlert(false);
            }
        }
    };

    // Normalize spacing fields (qtyPadding, qtyMargin) in a section object.
    // If the value is a JSON string, decode it to an object so the UI works with objects.
    const normalizeSpacingInSection = (sectionObj) => {
        if (!sectionObj || typeof sectionObj !== "object") return sectionObj;

        const copy = { ...sectionObj };

        [
            "qtyPadding",
            "qtyMargin",
            "sliderMargin",
            "prefixMargin",
            "suffixMargin",
        ].forEach((key) => {
            const val = copy[key];
            if (typeof val === "string" && val.trim() !== "") {
                try {
                    const parsed = JSON.parse(val);
                    if (parsed && typeof parsed === "object") {
                        copy[key] = parsed;
                    }
                } catch (e) {
                    // Leave as-is (could be an empty string or invalid) — UI will handle defaults
                }
            }
        });

        return copy;
    };

    // Update options data on page load
    useEffect(() => {
        if (
            fetchedSettings &&
            !(Array.isArray(fetchedSettings) && fetchedSettings.length === 0)
        ) {
            const newSettings = {
                wpbean_qc_settings_general: processSettings(
                    fetchedSettings.wpbean_qc_settings_general,
                    initialGeneralSettings,
                ),
                wpbean_qc_settings_input: processSettings(
                    fetchedSettings.wpbean_qc_settings_input,
                    initialInputSettings,
                ),
                wpbean_qc_settings_quantity: processSettings(
                    fetchedSettings.wpbean_qc_settings_quantity,
                    initialQuantitySettings,
                ),
                wpbean_qc_settings_slider: processSettings(
                    fetchedSettings.wpbean_qc_settings_slider,
                    initialSlideripSettings,
                ),
                wpbean_qc_settings_messages: processSettings(
                    fetchedSettings.wpbean_qc_settings_messages,
                    initialMessagesSettings,
                ),
                wpbean_qc_settings_icon: processSettings(
                    fetchedSettings.wpbean_qc_settings_icon,
                    initialIconSettings,
                ),
                wpbean_qc_settings_prefixsuffix: processSettings(
                    fetchedSettings.wpbean_qc_settings_prefixsuffix,
                    initialPrefixSuffixSettings,
                ),
            };
            // Ensure spacing fields are objects (not JSON strings) for the input section
            if (newSettings.wpbean_qc_settings_input) {
                newSettings.wpbean_qc_settings_input =
                    normalizeSpacingInSection(
                        newSettings.wpbean_qc_settings_input,
                    );
            }
            setSettings(newSettings);
            setOriginalSettings(newSettings); // <--- set original
        } else {
            setSettings(initialSettingsData);
            setOriginalSettings(initialSettingsData);
        }
    }, [fetchedSettings]);

    //console.log(fetchedSettings);

    // Build tabs and allow conditional visibility based on settings
    const _baseTabs = [
        {
            id: "general",
            label: __("General", "wpbean-quantity-controls-for-woocommerce"),
            icon: Settings,
        },
        {
            id: "quantity",
            label: __("Quantity", "wpbean-quantity-controls-for-woocommerce"),
            icon: PackagePlus,
        },
        {
            id: "input",
            label: __("Input", "wpbean-quantity-controls-for-woocommerce"),
            icon: TextCursorInput,
        },
        {
            id: "icon",
            label: __("Icons", "wpbean-quantity-controls-for-woocommerce"),
            icon: Diff,
        },
        {
            id: "slider",
            label: __("Slider", "wpbean-quantity-controls-for-woocommerce"),
            icon: Settings2,
        },
        {
            id: "messages",
            label: __(
                "Error Messages",
                "wpbean-quantity-controls-for-woocommerce",
            ),
            icon: OctagonAlert,
        },
        {
            id: "prefixsuffix",
            label: __(
                "Prefix & Suffix",
                "wpbean-quantity-controls-for-woocommerce",
            ),
            icon: UnfoldHorizontal,
        },
    ];

    const shouldShowTab = (tabId) => {
        // By default show the tab
        // For `input` and `icon` tabs, only show when qtyDisplayType for the general section is 'buttons'
        if (tabId === "input" || tabId === "icon") {
            const displayType = getOption("qtyDisplayType", "general");
            return displayType === "buttons";
        }

        if (tabId === "slider") {
            const displayType = getOption("qtyDisplayType", "general");
            return displayType === "slider";
        }

        return true;
    };

    const tabs = _baseTabs.filter((t) => shouldShowTab(t.id));

    // Toggle for boolean fields
    const handleToggle = (section, key) => {
        setSettings((prev) => {
            const sectionObj = prev[section] || {};
            const updatedSection = {
                ...sectionObj,
                [key]: !sectionObj?.[key],
            };

            const updated = {
                ...prev,
                [section]: updatedSection,
            };

            setHasChanges(!deepEqual(updated, originalSettings));
            return updated;
        });
    };

    // Input or select change
    const handleInputChange = (section, key, value) => {
        setSettings((prev) => {
            const updated = {
                ...prev,
                [section]: {
                    ...prev[section],
                    [key]: value,
                },
            };
            setHasChanges(!deepEqual(updated, originalSettings));

            // Validate quantity fields and update messages
            const updatedQuantitySettings = updated.wpbean_qc_settings_quantity;

            const min = Number(updatedQuantitySettings.quantityMin) || 0;
            const max = Number(updatedQuantitySettings.quantityMax) || 0;
            const step = Number(updatedQuantitySettings.quantityStep) || 0;
            const def = Number(updatedQuantitySettings.defaultQuantity) || 0;

            const messages = [];

            // 1) max must be greater than min
            if (max <= min) {
                messages.push(
                    __(
                        "Maximum quantity must be greater than minimum quantity.",
                        "wpbean-quantity-controls-for-woocommerce",
                    ),
                );
            }

            // 2) step > 0 and (if max > 0) step < max
            if (step <= 0) {
                messages.push(
                    __(
                        "Step must be greater than zero.",
                        "wpbean-quantity-controls-for-woocommerce",
                    ),
                );
            } else if (max > 0 && step >= max) {
                messages.push(
                    __(
                        "Step value should be less than the maximum quantity.",
                        "wpbean-quantity-controls-for-woocommerce",
                    ),
                );
            }

            // 3) default between min and max
            if (def < min) {
                messages.push(
                    __(
                        "Default quantity must be greater than or equal to the minimum quantity.",
                        "wpbean-quantity-controls-for-woocommerce",
                    ),
                );
            }
            if (max > 0 && def > max) {
                messages.push(
                    __(
                        "Default quantity must be less than or equal to the maximum quantity.",
                        "wpbean-quantity-controls-for-woocommerce",
                    ),
                );
            }

            // 4) default aligns with step interval from min
            if (step > 0 && def >= min) {
                const offset = def - min;
                if (offset % step !== 0) {
                    messages.push(
                        __(
                            "Default quantity does not match the step interval from minimum quantity.",
                            "wpbean-quantity-controls-for-woocommerce",
                        ),
                    );
                }
            }

            // Update validation messages state
            setQtyValidationMessages(messages);

            return updated;
        });
    };

    // Block Editor's BoxControl [SpaceControl] on change
    const handleSpaceControlChange = (values, section, key) => {
        const addUnit = (val) => {
            if (!val) return ""; // handle empty
            // If it's a number or a string without any unit, append "px"
            return /^\d+$/.test(val) ? `${val}px` : val;
        };

        const normalized = {
            top: addUnit(values.top ?? ""),
            right: addUnit(values.right ?? ""),
            bottom: addUnit(values.bottom ?? ""),
            left: addUnit(values.left ?? ""),
        };

        // Check if all values are empty
        const allEmpty =
            !normalized.top &&
            !normalized.right &&
            !normalized.bottom &&
            !normalized.left;

        // If all empty, pass empty string instead of object
        const finalValue = allEmpty ? "" : normalized;

        // reuse handleInputChange so hasChanges logic stays consistent
        handleInputChange(section, key, finalValue);
    };

    /**
     * Fetch Settings from REST API
     */
    const fetchSettings = useCallback(async () => {
        setLoading((prev) => ({ ...prev, settings: true }));
        //setError(null);
        try {
            const response = await apiFetch({
                path: "/wpb-pmqb-quantity/v1/settings",
            });

            setFetchedSettings(response || []);
        } catch (err) {
            console.error("Fetch settings error:", err);
            //setError(err.message || "Failed to fetch shortcodes");
            // addToast({
            //     type: "error",
            //     message: `Failed to fetch settings: ${
            //         err.message || "Unknown error"
            //     }`,
            // });
        } finally {
            setLoading((prev) => ({ ...prev, settings: false }));
        }
    }, []);

    // Load shortcodes on mount
    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    // Settings form submit and save the settings
    const handleSubmit = async () => {
        setLoading((prev) => ({ ...prev, updateSettings: true }));

        try {
            // Prepare payload: normalize spacing fields to objects before sending
            const payload = Object.fromEntries(
                Object.entries(settings).map(([sectionKey, sectionObj]) => [
                    sectionKey,
                    normalizeSpacingInSection(sectionObj),
                ]),
            );

            // Send all settings sections to REST API (each section is one option_name)
            const response = await apiFetch({
                path: "/wpb-pmqb-quantity/v1/settings",
                method: "POST",
                data: payload, // e.g. { form_settings: {...}, woo_settings: {...} }
            });

            if (response.success) {
                addToast({
                    type: "success",
                    message: response.message || "Settings saved successfully!",
                });

                setOriginalSettings(settings);
                setHasChanges(false);

                // console.log(
                //     "✅ Settings saved successfully:",
                //     response.updated
                // );
                // Optionally show a toast or success message
            } else {
                addToast({
                    type: "error",
                    message: `Failed to save settings: ${errorMessage}`,
                });

                console.error("⚠️ Failed to save settings:", response);
            }
        } catch (error) {
            console.error("❌ Error saving settings:", error);
        } finally {
            setLoading((prev) => ({ ...prev, updateSettings: false }));
            setHasChanges(false); // Hide save button bar after save
        }
    };

    const ctxValue = {
        hasChanges,
        loading,
        tabs,
        activeTab,
        setActiveTab,
        settings,
        setSettings,
        sidebarOpen,
        setSidebarOpen,
        handleToggle,
        handleInputChange,
        handleSpaceControlChange,
        handleSubmit,
        addToast,
        removeToast,
        clearAllToasts,
        toasts,
        getSection,
        getOption,
        quantity,
        setQuantity,
        showMinAlert,
        setShowMinAlert,
        showMaxAlert,
        setShowMaxAlert,
        previewOptions,
        handleDecrease,
        handleIncrease,
        handleQtyInputChange,
        qtyValidationMessages,
    };

    return (
        <SettingsContext.Provider value={ctxValue}>
            {children}
        </SettingsContext.Provider>
    );
}
